<template>
    <section class="l2-screenshots" :class="{ 'l2-screenshots--embedded': embedded }" :id="embedded ? undefined : 'screenshots'">
        <div class="l2-screenshots__container">
            <div class="l2-screenshots__gallery">
                <!-- Main screenshot display -->
                <div class="l2-screenshots__main">
                    <div class="l2-screenshots__frame">
                        <div class="l2-screenshots__frame-header">
                            <span class="l2-screenshots__frame-dot"></span>
                            <span class="l2-screenshots__frame-dot"></span>
                            <span class="l2-screenshots__frame-dot"></span>
                        </div>
                        <button
                            ref="imageWrapper"
                            type="button"
                            class="l2-screenshots__image-wrapper"
                            :aria-label="`Expand ${screenshots[activeIndex].title}`"
                            @click="openLightbox">
                            <Transition name="l2-screenshot-fade" mode="out-in">
                                <img
                                    :key="`${activeIndex}-${theme}`"
                                    :src="getThemedImageSrc(screenshots[activeIndex].image)"
                                    :alt="screenshots[activeIndex].title"
                                    loading="lazy"
                                    decoding="async"
                                    class="l2-screenshots__image" />
                            </Transition>
                            <div class="l2-screenshots__expand-hint">
                                <v-icon size="20">mdi-magnify-expand</v-icon>
                                <span>Click to expand</span>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Screenshot info -->
                <div class="l2-screenshots__info">
                    <Transition name="l2-info-fade" mode="out-in">
                        <div :key="activeIndex" class="l2-screenshots__info-content">
                            <h3 class="l2-screenshots__info-title">{{ screenshots[activeIndex].title }}</h3>
                            <p class="l2-screenshots__info-description">{{ screenshots[activeIndex].description }}</p>
                        </div>
                    </Transition>
                </div>

                <!-- Navigation dots -->
                <div class="l2-screenshots__nav">
                    <button
                        v-for="(screenshot, index) in screenshots"
                        :key="screenshot.id"
                        type="button"
                        class="l2-screenshots__dot"
                        :class="{ 'l2-screenshots__dot--active': index === activeIndex }"
                        @click="setActive(index)"
                        :aria-label="`View ${screenshot.title}`">
                        <span class="l2-screenshots__dot-progress" v-if="index === activeIndex && isPlaying"></span>
                    </button>
                </div>

                <!-- Controls -->
                <div class="l2-screenshots__controls">
                    <v-btn icon size="small" variant="outlined" class="l2-control-btn" @click="prev" aria-label="Previous screenshot">
                        <v-icon size="20">mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-btn icon size="small" variant="outlined" class="l2-control-btn" @click="togglePlay" :aria-label="isPlaying ? 'Pause' : 'Play'">
                        <v-icon size="18">{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                    </v-btn>
                    <v-btn icon size="small" variant="outlined" class="l2-control-btn" @click="next" aria-label="Next screenshot">
                        <v-icon size="20">mdi-chevron-right</v-icon>
                    </v-btn>
                </div>
            </div>
        </div>

        <!-- Lightbox overlay -->
        <v-dialog v-model="lightboxOpen" max-width="960" content-class="l2-lightbox">
            <DialogLayout
                :title="screenshots[activeIndex].title"
                body-class="pa-0"
                card-class="landing-dialog-card l2-lightbox-card"
                @close="closeLightbox">
                <div class="l2-lightbox__content pa-4">
                    <div class="l2-lightbox__image-frame">
                        <img
                            :src="getThemedImageSrc(screenshots[activeIndex].image)"
                            :alt="screenshots[activeIndex].title"
                            class="l2-lightbox__image" />
                    </div>

                    <div class="l2-lightbox__description text-body-2 mt-4">
                        {{ screenshots[activeIndex].description }}
                    </div>

                    <div class="l2-lightbox__controls d-flex align-center justify-space-between mt-3">
                        <div class="d-flex align-center ga-2">
                            <v-btn icon variant="text" aria-label="Previous" @click.stop="prev">
                                <v-icon size="24">mdi-chevron-left</v-icon>
                            </v-btn>
                            <v-btn icon variant="text" aria-label="Next" @click.stop="next">
                                <v-icon size="24">mdi-chevron-right</v-icon>
                            </v-btn>
                        </div>

                        <v-btn variant="text" @click="closeLightbox">Close</v-btn>
                    </div>
                </div>
            </DialogLayout>
        </v-dialog>
    </section>
</template>

<script lang="ts" setup>
    import DialogLayout from '@/components/global/DialogLayout.vue';
    import type { LandingTheme } from '@/pages/Landing/composables/useLandingTheme';
    import type { LandingScreenshot, ThemedLandingAsset } from '@/pages/Landing/landingScreenshots';
    import { getThemedLandingImageSrc } from '@/pages/Landing/landingScreenshots';
    import { onMounted, onUnmounted, ref, watch } from 'vue';

    const props = defineProps<{
        screenshots: LandingScreenshot[];
        theme: LandingTheme;
        embedded?: boolean;
    }>();

    const SLIDE_INTERVAL = 8000; // 8 seconds per slide

    const getThemedImageSrc = (image: ThemedLandingAsset) => getThemedLandingImageSrc(image, props.theme);

    const activeIndex = ref(0);
    const isPlaying = ref(true);
    const lightboxOpen = ref(false);
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const imageWrapper = ref<HTMLElement | null>(null);
    const galleryVisible = ref(false);
    let intersectionObserver: IntersectionObserver | null = null;

    const preloadedSrcs = new Set<string>();

    const scheduleIdle = (callback: () => void) => {
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            window.requestIdleCallback(callback, { timeout: 1500 });
            return;
        }

        setTimeout(callback, 250);
    };

    const preloadImage = (src: string) => {
        if (preloadedSrcs.has(src)) {
            return;
        }

        preloadedSrcs.add(src);
        const img = new Image();
        img.decoding = 'async';
        img.loading = 'eager';

        if ('fetchPriority' in img) {
            (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'low';
        }

        img.src = src;
    };

    const preloadAhead = (count: number) => {
        const length = props.screenshots.length;
        if (length < 2) {
            return;
        }

        for (let offset = 1; offset <= count; offset += 1) {
            const screenshot = props.screenshots[(activeIndex.value + offset) % length];
            if (!screenshot) {
                continue;
            }

            scheduleIdle(() => preloadImage(getThemedImageSrc(screenshot.image)));
        }
    };

    const restartInterval = () => {
        if (isPlaying.value && galleryVisible.value) {
            startInterval();
        }
    };

    const setActive = (index: number) => {
        activeIndex.value = index;
        restartInterval();
    };

    const next = () => {
        if (props.screenshots.length === 0) {
            return;
        }

        activeIndex.value = (activeIndex.value + 1) % props.screenshots.length;
        restartInterval();
    };

    const prev = () => {
        if (props.screenshots.length === 0) {
            return;
        }

        activeIndex.value = (activeIndex.value - 1 + props.screenshots.length) % props.screenshots.length;
        restartInterval();
    };

    const togglePlay = () => {
        isPlaying.value = !isPlaying.value;
        if (isPlaying.value) {
            if (galleryVisible.value) {
                startInterval();
            }
        } else {
            stopInterval();
        }
    };

    const startInterval = () => {
        stopInterval();
        if (props.screenshots.length < 2) {
            return;
        }

        intervalId = setInterval(() => {
            activeIndex.value = (activeIndex.value + 1) % props.screenshots.length;
        }, SLIDE_INTERVAL);
    };

    const stopInterval = () => {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    };

    const openLightbox = () => {
        lightboxOpen.value = true;
        stopInterval();
    };

    const closeLightbox = () => {
        lightboxOpen.value = false;
        if (isPlaying.value && galleryVisible.value) {
            startInterval();
        }
    };

    onMounted(() => {
        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
            galleryVisible.value = true;
            preloadAhead(3);
            if (isPlaying.value) {
                startInterval();
            }
            return;
        }

        intersectionObserver = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    galleryVisible.value = true;
                    preloadAhead(3);
                    if (isPlaying.value) {
                        startInterval();
                    }
                    intersectionObserver?.disconnect();
                    intersectionObserver = null;
                }
            },
            { threshold: 0.15 },
        );

        if (imageWrapper.value) {
            intersectionObserver.observe(imageWrapper.value);
        }
    });

    onUnmounted(() => {
        stopInterval();
        intersectionObserver?.disconnect();
        intersectionObserver = null;
    });

    watch([activeIndex, galleryVisible, () => props.theme], () => {
        if (!galleryVisible.value) {
            return;
        }

        preloadAhead(3);
    });
</script>

<style lang="scss" scoped>
    .l2-screenshots {
        position: relative;
        padding: var(--l2-section-padding-y) 24px;
        background: var(--l2-bg);

        &--embedded {
            padding: 0;
            background: transparent;
        }

        &__container {
            max-width: 1280px;
            margin: 0 auto;
        }

        &__header {
            text-align: center;
            margin-bottom: 60px;
        }

        &__title {
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 700;
            line-height: 1.2;
            color: var(--l2-text);
        }

        &__title-accent {
            color: var(--landing-muted);
        }

        &__gallery {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 32px;
        }

        &__main {
            width: 100%;
            max-width: 1000px;
        }

        &__frame {
            background: var(--l2-surface);
            border-radius: var(--l2-radius);
            overflow: hidden;
            box-shadow:
                0 0 0 1px var(--l2-border-subtle),
                0 40px 100px -30px rgba(0, 0, 0, 0.6),
                0 0 80px var(--l2-accent-soft);
        }

        &__frame-header {
            display: flex;
            gap: 8px;
            padding: 14px 18px;
            background: var(--l2-bg-elevated);
            border-bottom: 1px solid var(--l2-border-subtle);
        }

        &__frame-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: var(--l2-border-subtle);

            &:first-child {
                background: #ef4444;
            }

            &:nth-child(2) {
                background: #f59e0b;
            }

            &:nth-child(3) {
                background: #22c55e;
            }
        }

        &__image-wrapper {
            position: relative;
            overflow: hidden;
            background: var(--l2-bg);
            cursor: pointer;
            width: 100%;
            aspect-ratio: 1912 / 973;
            border: 0;
            padding: 0;
            display: block;
            text-align: left;

            &:hover {
                .l2-screenshots__expand-hint {
                    opacity: 1;
                }
            }

            &:focus-visible {
                outline: 2px solid var(--l2-accent);
                outline-offset: 3px;
            }
        }

        &__image {
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: block;
        }

        &__expand-hint {
            position: absolute;
            bottom: 16px;
            right: 16px;
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            background: var(--l2-expand-hint-bg);
            border-radius: 8px;
            color: var(--l2-expand-hint-text);
            font-size: 0.8125rem;
            opacity: 0;
            transition: opacity var(--l2-transition);
            pointer-events: none;

            .v-icon {
                color: var(--l2-expand-hint-icon) !important;
            }
        }

        &__info {
            text-align: center;
            min-height: 80px;
        }

        &__info-content {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        &__info-title {
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--l2-text);
            margin: 0;
        }

        &__info-description {
            font-size: 1rem;
            color: var(--l2-text-muted);
            margin: 0;
        }

        &__nav {
            display: flex;
            gap: 12px;
            justify-content: center;
            flex-wrap: wrap;
        }

        &__dot {
            position: relative;
            width: 48px;
            height: 4px;
            background: var(--l2-border-subtle);
            border: none;
            border-radius: 4px;
            cursor: pointer;
            overflow: hidden;
            transition: background var(--l2-transition);

            &--active {
                background: var(--l2-accent-soft);
            }

            &:hover:not(&--active) {
                background: var(--l2-surface);
            }
        }

        &__dot-progress {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            background: var(--l2-accent);
            animation: l2-progress 8s linear forwards;
        }

        &__controls {
            display: flex;
            gap: 8px;
        }
    }

    .l2-control-btn {
        border-color: var(--l2-border-subtle) !important;
        color: var(--l2-text) !important;
        transition: all var(--l2-transition);

        &:hover {
            background: var(--l2-accent-soft) !important;
            border-color: var(--l2-border) !important;
        }

        .v-icon {
            color: var(--l2-text) !important;
        }
    }

    @media (max-width: 600px) {
        .l2-screenshots__gallery {
            gap: 18px;
        }

        .l2-screenshots__nav {
            display: none;
        }

        .l2-screenshots__info {
            min-height: 96px;
        }

        .l2-screenshots__info-description {
            line-height: 1.4;
            min-height: 2.8em;
            max-height: 2.8em;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
    }

    // Lightbox styles
    :deep(.l2-lightbox-card) {
        background: var(--l2-lightbox-bg) !important;
    }

    :deep(.l2-lightbox-card .v-card-title) {
        background: var(--l2-bg-elevated) !important;
    }

    :deep(.l2-lightbox) {
        width: min(92vw, 960px);
    }

    .l2-lightbox__content {
        background: var(--l2-lightbox-bg);
    }

    .l2-lightbox__image-frame {
        width: 100%;
        margin: 0 auto;
        aspect-ratio: 1912 / 973;
        border-radius: var(--l2-radius-sm);
        border: 1px solid var(--l2-border-subtle);
        background: var(--l2-bg-elevated);
        box-shadow:
            0 0 0 1px rgba(0, 0, 0, 0.03),
            0 30px 70px -45px rgba(0, 0, 0, 0.5);
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .l2-lightbox__image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }

    .l2-lightbox__description {
        height: 104px;
        overflow-y: auto;
        color: var(--l2-text-muted);
    }

    .l2-lightbox__controls {
        border-top: 1px solid var(--l2-border-subtle);
        padding-top: 12px;
    }

    @media (max-width: 600px) {
        .l2-lightbox__content {
            padding: 16px !important;
        }

        .l2-lightbox__description {
            height: 96px;
        }
    }
    :deep(.l2-lightbox-card.dialog-card) {
        max-height: 92vh;
    }

    // Transitions
    .l2-screenshot-fade-enter-active,
    .l2-screenshot-fade-leave-active {
        transition:
            opacity 0.4s ease,
            transform 0.4s ease;
    }

    .l2-screenshot-fade-enter-from {
        opacity: 0;
        transform: scale(1.02);
    }

    .l2-screenshot-fade-leave-to {
        opacity: 0;
        transform: scale(0.98);
    }

    .l2-info-fade-enter-active,
    .l2-info-fade-leave-active {
        transition:
            opacity 0.3s ease,
            transform 0.3s ease;
    }

    .l2-info-fade-enter-from {
        opacity: 0;
        transform: translateY(10px);
    }

    .l2-info-fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }

    @keyframes l2-progress {
        from {
            width: 0%;
        }
        to {
            width: 100%;
        }
    }
</style>
